import React, { useCallback, useLayoutEffect, useState } from 'react'
import Icon from '@/components/common/Icon.tsx'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '@/utils/query-keys'
import { getFolderContent } from '@/features/media-file/domain/use-cases/get-folder-content'
import type { MediaFolder } from '@/features/media-file/domain/models/media-folder'
import Spinner from '@/components/common/Spinner'
import type { MediaFile } from '@/features/media-file/domain/models/media-file.ts'
import {
  formatBytesToSize,
  formatSecondsToMinutesSeconds,
} from '@/utils/formatters'
import useSpeechDialogStore from './speech-dialog.store.ts'
import PlayButton from '@/components/Sidebar/Scripts/PlayButton'
import ContextMenu from '@/components/common/ContextMenu'
import DeleteFileConfirmationDialog from '@/components/Sidebar/MediaFiles/DeleteFileConfirmationDialog'
import MediaFilesPathchain from '../Sidebar/MediaFiles/MediaFilesPathchain.tsx'
import type { UUID } from '@/features/shared/data/string.types.ts'

const useFolderNavigation = () => {
  const queryClient = useQueryClient()
  const {
    mediaFilesCurrentFolder: currentFolder,
    setMediaFilesCurrentFolder: setCurrentFolder,
    setSelectedLibraryFiles,
  } = useSpeechDialogStore()
  const [loadingFolder, setLoadingFolder] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const navigateToFolder = useCallback(
    async (folderId: string | undefined, loadingKey: string) => {
      setIsLoading(true)
      setLoadingFolder(loadingKey)
      try {
        const data = await getFolderContent(folderId)
        setCurrentFolder(data)
        setSelectedLibraryFiles([])

        // Invalidate the main query to force it to refetch
        void queryClient.invalidateQueries({
          queryKey: [QueryKeys.MediaFolderContent, folderId],
        })
      } catch (error) {
        console.error('Navigation failed:', error)
      } finally {
        setLoadingFolder(null)
        setIsLoading(false)
      }
    },
    [setCurrentFolder, setSelectedLibraryFiles, queryClient]
  )

  // Load root folder on mount if no folder is selected
  useLayoutEffect(() => {
    if (!currentFolder) {
      navigateToFolder(undefined, 'initial-load')
    }
  }, [currentFolder, navigateToFolder])

  return { currentFolder, loadingFolder, navigateToFolder, isLoading }
}

const gridTemplateColumns = '320px 152px 154px 154px 116px'

interface LibFolderProps {
  folder?: MediaFolder | undefined
  isLoading?: boolean
  onClick: () => void
  isFolderUp?: true
}

const LibFolder: React.FC<LibFolderProps> = ({
  folder,
  isLoading,
  onClick,
  isFolderUp,
}) => {
  return (
    <div
      className={`group grid cursor-pointer items-center ${
        isFolderUp ? 'text-blue-500' : ''
      }`}
      style={{ gridTemplateColumns }}
      onClick={() => {
        onClick()
      }}
    >
      <div className="flex h-[52px] items-center gap-2 border-r border-zinc-800 px-4 group-hover:border-r-0 group-hover:bg-white/[6%]">
        {isLoading ? (
          <Spinner className="h-5 w-5" />
        ) : (
          <Icon
            name="folderSolid"
            width={20}
            height={20}
            fill={isFolderUp ? '#3b82f6' : '#fff'}
          />
        )}
        <span className="truncate">{folder?.name ?? ''}</span>
      </div>
      <div className="h-[52px] border-r border-zinc-800 px-4 group-hover:border-r-0 group-hover:bg-white/[6%]" />
      <div className="h-[52px] border-r border-zinc-800 px-4 group-hover:border-r-0 group-hover:bg-white/[6%]" />
      <div className="h-[52px] border-r border-zinc-800 px-4 group-hover:border-r-0 group-hover:bg-white/[6%]" />
      <div className="flex h-[52px] items-center justify-center px-4 group-hover:bg-white/[6%]" />
    </div>
  )
}

export interface LibFileProps {
  file: MediaFile
  isSelected: boolean
  folderId: UUID
  onClick: () => void
}

const LibFile: React.FC<LibFileProps> = ({
  file: { id, duration, category, name, size },
  folderId,
  isSelected,
  onClick,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const menuButtonRef = React.useRef<HTMLButtonElement>(null)
  const colClassName = `flex h-[52px] items-center gap-2 border-r border-zinc-800 px-4 border-r-0 ${
    isSelected ? 'bg-white/10' : 'group-hover:bg-white/[6%]'
  }`

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsMenuOpen(false)
    setShowDeleteConfirmation(true)
  }

  return (
    <>
      <DeleteFileConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        fileId={id}
        folderId={folderId}
      />
      <div
        className="group grid cursor-pointer items-center"
        style={{ gridTemplateColumns }}
        onClick={onClick}
      >
        <div className={colClassName}>
          <span className="truncate text-sm font-medium text-white">
            {name}
          </span>
        </div>
        <div className={colClassName}>{category?.name || 'Other'}</div>
        <div className={colClassName}>{size && formatBytesToSize(size)}</div>
        <div className={colClassName}>
          {duration && formatSecondsToMinutesSeconds(duration)}
        </div>
        <div className={`${colClassName} relative justify-center gap-3`}>
          <PlayButton soundId={id} />
          <button
            type="button"
            ref={menuButtonRef}
            className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/10 focus:outline-none"
            onClick={(e) => {
              e.stopPropagation()
              setIsMenuOpen((open) => !open)
            }}
          >
            <Icon name="menuHorizontal" width={20} height={20} />
          </button>
          <ContextMenu
            viewModel={[
              {
                key: 'Delete',
                action: handleDelete,
                icon: <Icon name="trash" width={18} height={18} />,
              },
            ]}
            isOpen={isMenuOpen}
            setIsOpen={setIsMenuOpen}
            position={{ right: 14, top: 44 }}
            triggerRef={menuButtonRef}
          />
        </div>
      </div>
    </>
  )
}

const sortableColumns = ['name', 'category', 'size', 'duration'] as const
type SortableColumn = (typeof sortableColumns)[number]
type SortableDirection = 'asc' | 'desc'

const MediaLibrary: React.FC = () => {
  const { t } = useTranslation()
  const { selectedLibraryFiles, setSelectedLibraryFiles } =
    useSpeechDialogStore()
  const { currentFolder, loadingFolder, navigateToFolder, isLoading } =
    useFolderNavigation()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [sortedColumn, setSortedColumn] = useState<SortableColumn>('name')
  const [sortedDirection, setSortedDirection] =
    useState<SortableDirection>('asc')
  const isRoot = currentFolder?.pathChain.length === 1
  const parentFolderId = currentFolder?.parentFolder?.id

  // Determine the parent folder name for the "go up" link
  const getParentFolderName = () => {
    if (!currentFolder?.pathChain || currentFolder.pathChain.length <= 1) {
      return undefined
    }

    // If pathChain length is 2, the parent is root
    if (currentFolder.pathChain.length === 2) {
      return t('ls_generic_home')
    }

    // Otherwise, get the actual parent folder name (second to last in pathChain)
    return currentFolder.pathChain[currentFolder.pathChain.length - 2]?.name
  }

  const parentFolderName = getParentFolderName()

  const isSortedBy = useCallback(
    (column: string, direction: 'asc' | 'desc') => {
      return sortedColumn === column && sortedDirection === direction
    },
    [sortedColumn, sortedDirection]
  )

  const handleSort = useCallback(
    (column: SortableColumn) => {
      if (sortedColumn === column) {
        setSortedDirection(sortedDirection === 'asc' ? 'desc' : 'asc')
      } else {
        setSortedColumn(column)
        setSortedDirection('asc')
      }
    },
    [sortedColumn, sortedDirection]
  )

  const handleBackHome = useCallback(() => {
    if (isRoot) return
    navigateToFolder(undefined, 'root')
  }, [isRoot, navigateToFolder])

  const handleBack = useCallback(() => {
    if (isRoot) return
    navigateToFolder(parentFolderId, parentFolderId || 'parent')
  }, [isRoot, parentFolderId, navigateToFolder])

  const handleFolderClick = useCallback(
    (folder: MediaFolder) => {
      navigateToFolder(folder.id, folder.id || 'folder')
    },
    [navigateToFolder]
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value.trim())
    },
    []
  )

  const filterFn = useCallback(
    (data: MediaFolder | MediaFile) => {
      return (
        data.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
      )
    },
    [searchQuery]
  )

  const sortFn = useCallback(
    (a: MediaFile, b: MediaFile) => {
      const dir = sortedDirection === 'asc' ? 1 : -1

      switch (sortedColumn) {
        case 'name':
          return a.name.localeCompare(b.name) * dir
        case 'category':
          return (
            (a.category.name ?? '').localeCompare(b.category.name ?? '') * dir
          )
        case 'size':
          return ((a.size ?? 0) - (b.size ?? 0)) * dir
        case 'duration':
          return ((a.duration ?? 0) - (b.duration ?? 0)) * dir
        default:
          return 0
      }
    },
    [sortedColumn, sortedDirection]
  )

  return (
    <div className="flex max-h-[560px] flex-1 flex-col overflow-hidden rounded-lg bg-zinc-800">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 border-b border-zinc-800 p-4">
        <div className="flex flex-1 items-center gap-1">
          <span
            className="flex cursor-pointer gap-1 text-gray-300 hover:text-white"
            onClick={handleBackHome}
          >
            <Icon name="home" width={20} height={20} />
            <span className="text-sm font-medium">
              {t('ls_generic_home')} /
            </span>
          </span>
          {currentFolder?.pathChain && (
            <MediaFilesPathchain chain={currentFolder.pathChain} />
          )}
        </div>
        <div className="relative flex flex-1 items-center gap-2 overflow-hidden rounded-md border border-zinc-600">
          <div className="pointer-events-none flex items-center pl-4">
            <Icon name="search" width={20} height={20} />
          </div>
          <input
            type="text"
            className="block w-full bg-zinc-800 py-3 pl-0 pr-4 text-sm text-white placeholder:text-sm placeholder:font-medium placeholder:text-gray-300 focus:outline-none"
            placeholder={t('ls_speech_dialog_library_search_placeholder')}
            onChange={handleSearchChange}
          />
        </div>
      </div>
      {/* Table Header */}
      <div
        className="grid border-b border-zinc-800 bg-zinc-700 text-sm font-semibold text-white"
        style={{ gridTemplateColumns }}
      >
        {sortableColumns.map((column) => (
          <div
            key={column}
            role="columnheader"
            aria-sort={
              isSortedBy(column, 'asc')
                ? 'ascending'
                : isSortedBy(column, 'desc')
                  ? 'descending'
                  : 'none'
            }
            className="flex h-[52px] cursor-pointer items-center justify-between border-r border-zinc-800 px-4"
            onClick={() => handleSort(column)}
          >
            <span className="capitalize">{column}</span>
            <span className="ml-2 flex flex-col">
              <span
                className={isSortedBy(column, 'asc') ? 'text-blue-400' : ''}
              >
                <Icon name="chevronUp" width={12} height={12} />
              </span>
              <span
                className={isSortedBy(column, 'desc') ? 'text-blue-400' : ''}
              >
                <Icon name="chevronDown" width={12} height={12} />
              </span>
            </span>
          </div>
        ))}

        <div className="flex h-[52px] items-center justify-center">
          <span>Actions</span>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden text-sm font-medium text-white">
        {/* Parent folder */}
        {parentFolderId && (
          <LibFolder
            isLoading={loadingFolder === parentFolderId}
            folder={{ name: parentFolderName } as MediaFolder}
            onClick={handleBack}
            isFolderUp
          />
        )}
        {/* folders */}
        {(currentFolder?.folders || [])
          ?.filter(filterFn)
          .map((folder) => (
            <LibFolder
              key={folder.id}
              isLoading={loadingFolder === folder.id}
              folder={folder}
              onClick={() => handleFolderClick(folder)}
            />
          ))}
        {/* files */}
        {(currentFolder?.files || [])
          ?.filter(filterFn)
          .sort(sortFn)
          .map((file) => (
            <LibFile
              folderId={currentFolder?.folderId as UUID}
              isSelected={selectedLibraryFiles.includes(file)}
              key={file.id}
              file={file}
              onClick={() =>
                selectedLibraryFiles.includes(file)
                  ? setSelectedLibraryFiles(
                      selectedLibraryFiles.filter((f) => f.id !== file.id)
                    )
                  : setSelectedLibraryFiles([...selectedLibraryFiles, file])
              }
            />
          ))}
        {/* no content */}
        {(currentFolder?.folders || []).length === 0 &&
          (currentFolder?.files || []).length === 0 &&
          !isLoading && (
            <p className="flex h-[52px] items-center justify-center text-center text-gray-500">
              {t('ls_sidebar_media_files_no_content')}
            </p>
          )}
        {/* loading */}
        {isLoading && (
          <div className="flex h-20 w-full items-center justify-center">
            <Spinner className="h-6 w-6" />
          </div>
        )}
      </div>
    </div>
  )
}

export default MediaLibrary
